import React, { useState, useMemo, useEffect } from "react";
import { Button } from "react-md";
import ZypleRadio from "../../../../../../commonComponents/ZypleRadio";
import InfoButton from "../../../../account-module/helper/InfoButton";
import CartWalletRulesInfoBox from "./CartWalletRulesInfoBox";
import {
  CART_SCREEN_STATE,
  CART_PAYMENT_METHOD,
  getCartRefund,
  getCartPaymentDetails,
  formatCurrency,
} from "../cartPaymentLinkConstants";

/**
 * Method-select screen of the cart approve flow. Mirrors the appointment
 * SelectPaymentMethod: options are derived from the preview `sublimit` block
 * (wallet/razorpay split + balance + rules), and the info icon reveals the
 * wallet-rules detail panel.
 *
 * `sublimit.useZylaWallet` / `useExternalWallet` are the TARGET split (they sum
 * to grandTotal), not the delta — the backend computes the actual amount to
 * collect/refund from the cart's payment history on approve. useWallet sent to
 * /approve = the selected method !== RAZORPAY_ONLY.
 */
const CartSelectPaymentMethod = ({ preview, paymentMethod, setPaymentMethod, goNext }) => {
  const [showWalletRules, setShowWalletRules] = useState(false);

  const sublimit = preview?.sublimit;
  const refund = getCartRefund(preview);

  // The plan has a wallet but this cart may not spend it, because of the family member it
  // is booked for (cart-service's helpers.EvaluateWalletFamilyGate). The backend already
  // routes the whole target to external; this keeps the reviewer from selecting a wallet
  // option that /approve would 409 on. The reason is stated once on the cart screen, under
  // "Booking for" — this dialog only acts on it.
  const walletBlockedForMember = sublimit?.walletBlockedForMember === true;
  const isWalletIncluded = sublimit?.isWalletIncluded && !walletBlockedForMember;
  const isUnlimited = sublimit?.isUnlimitedWallet;
  const useZylaWallet = sublimit?.useZylaWallet ?? 0;
  const useExternalWallet = sublimit?.useExternalWallet ?? 0;
  const available = sublimit?.zylaWallet?.available ?? 0;
  const grandTotal = sublimit?.grandTotal ?? 0;

  // The RAZORPAY_ONLY (useWallet=false) net collection. In the rebalance case
  // (preview carries `noWalletAlternative`) this is only the NET external top-up
  // and triggers no refund; for plain carts it falls back to the sublimit
  // grandTotal.
  const razorpayOnlyAmount = getCartPaymentDetails(preview, false).grandTotal;
  const hasNoWalletAlternative = !!preview?.noWalletAlternative;

  const isWalletAndRazorpayTooSmall =
    useExternalWallet > 0 && useExternalWallet < 1;
  const isRazorpayOnlyTooSmall =
    razorpayOnlyAmount > 0 && razorpayOnlyAmount < 1;

  const validOptions = useMemo(() => {
    if (!isWalletIncluded) return [CART_PAYMENT_METHOD.RAZORPAY_ONLY];

    if (isUnlimited && useZylaWallet === grandTotal)
      return [CART_PAYMENT_METHOD.RAZORPAY_ONLY, CART_PAYMENT_METHOD.WALLET_ONLY];

    // Limited wallet
    if (useZylaWallet === grandTotal)
      return [CART_PAYMENT_METHOD.RAZORPAY_ONLY, CART_PAYMENT_METHOD.WALLET_ONLY];
    if (useZylaWallet > 0 && useZylaWallet < grandTotal)
      return [
        CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY,
        CART_PAYMENT_METHOD.RAZORPAY_ONLY,
      ];
    if (available <= 0 && useZylaWallet <= 0)
      return [
        CART_PAYMENT_METHOD.RAZORPAY_ONLY,
        CART_PAYMENT_METHOD.WALLET_DISABLED,
      ];

    return [CART_PAYMENT_METHOD.RAZORPAY_ONLY];
  }, [isWalletIncluded, isUnlimited, useZylaWallet, available, grandTotal]);

  // Default/reset selection. Prefer a selectable wallet option (Wallet-only or
  // Wallet+Razorpay) wherever one exists, so "Pay via Wallet" is the default;
  // otherwise fall back to the first selectable (not too-small, not disabled)
  // option.
  useEffect(() => {
    if (paymentMethod && validOptions?.includes(paymentMethod)) return;

    const isSelectable = (option) => {
      if (option === CART_PAYMENT_METHOD.WALLET_DISABLED) return false;
      if (
        option === CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
        isWalletAndRazorpayTooSmall
      )
        return false;
      if (
        option === CART_PAYMENT_METHOD.RAZORPAY_ONLY &&
        isRazorpayOnlyTooSmall
      )
        return false;
      return true;
    };

    const selectable = validOptions?.filter(isSelectable) || [];
    const walletFirst = selectable.find(
      (option) =>
        option === CART_PAYMENT_METHOD.WALLET_ONLY ||
        option === CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY,
    );

    setPaymentMethod(walletFirst || selectable[0] || -1);
  }, [validOptions, isWalletAndRazorpayTooSmall, isRazorpayOnlyTooSmall]);

  return (
    <div className="pld-screen pld-method-container">
      <span className="pld-title">Choose a payment method:</span>

      {/* OPTION 1 - Wallet + Razorpay */}
      {validOptions?.includes(CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY}
              onChange={() =>
                setPaymentMethod(CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY)
              }
              value={CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY}
              name="cart-pay-radio"
            />
            <span className="pld-option-text">
              Pay via Wallet and Razorpay payment link
            </span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(useZylaWallet)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <CartWalletRulesInfoBox transactionData={sublimit} />
            )}
            <div>
              • via Razorpay:{" "}
              <span className="pld-bold">
                {formatCurrency(useExternalWallet)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OPTION 4 - Wallet Disabled */}
      {validOptions?.includes(CART_PAYMENT_METHOD.WALLET_DISABLED) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === CART_PAYMENT_METHOD.WALLET_DISABLED}
              onChange={() =>
                setPaymentMethod(CART_PAYMENT_METHOD.WALLET_DISABLED)
              }
              value={CART_PAYMENT_METHOD.WALLET_DISABLED}
              name="cart-pay-radio"
              disabled
            />
            <span className="pld-option-text-disabled">Pay via Wallet</span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center-disabled">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(0)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <CartWalletRulesInfoBox transactionData={sublimit} />
            )}
          </div>
        </div>
      )}

      {/* OPTION 2 - Razorpay Only */}
      {validOptions?.includes(CART_PAYMENT_METHOD.RAZORPAY_ONLY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY}
              onChange={() =>
                setPaymentMethod(CART_PAYMENT_METHOD.RAZORPAY_ONLY)
              }
              value={CART_PAYMENT_METHOD.RAZORPAY_ONLY}
              name="cart-pay-radio"
            />
            <span className="pld-option-text">
              Pay via Razorpay payment link
            </span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Razorpay:
              <span className="pld-bold margin-left-4">
                {formatCurrency(razorpayOnlyAmount)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* OPTION 3 - Wallet Only */}
      {validOptions?.includes(CART_PAYMENT_METHOD.WALLET_ONLY) && (
        <div className="pld-option">
          <div className="pld-option-main">
            <ZypleRadio
              checked={paymentMethod === CART_PAYMENT_METHOD.WALLET_ONLY}
              onChange={() => setPaymentMethod(CART_PAYMENT_METHOD.WALLET_ONLY)}
              value={CART_PAYMENT_METHOD.WALLET_ONLY}
              name="cart-pay-radio"
            />
            <span className="pld-option-text">Pay via Wallet</span>
          </div>

          <div className="pld-subitems">
            <div className="pld-flex-center">
              • via Wallet:
              <span className="pld-bold margin-left-4">
                {formatCurrency(grandTotal)}
              </span>
              <InfoButton
                size={14}
                onClick={() => setShowWalletRules(!showWalletRules)}
              />
            </div>
            {showWalletRules && (
              <CartWalletRulesInfoBox transactionData={sublimit} />
            )}
          </div>
        </div>
      )}

      {/* Rebalance refund the wallet path triggers. Shown in a red-themed box
          (mirrors the payment-details dialog) so ops clearly sees the refund the
          approve will also trigger. Hidden when RAZORPAY_ONLY is selected in the
          rebalance case: that branch preserves prior wallet and only tops up
          external — no refund happens. */}
      {refund &&
        (refund.wallet > 0 || refund.external > 0) &&
        !(
          paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY &&
          hasNoWalletAlternative
        ) && (
        <div className="pld-refund-container">
          <div className="pld-refund-box">
            <i className="material-icons pld-refund-icon">arrow_downward</i>
            <div className="pld-refund-lines">
              {refund.wallet > 0 && (
                <div>
                  Refund to Wallet:{" "}
                  <span className="pld-bold">
                    {formatCurrency(refund.wallet)}
                  </span>
                </div>
              )}
              {refund.external > 0 && (
                <div>
                  Refund to Razorpay:{" "}
                  <span className="pld-bold">
                    {formatCurrency(refund.external)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NOTE BOX */}
      <div className="pld-note-container">
        <div className="pld-note-box">
          <span className="pld-bold">Note:&nbsp;</span>
          {(paymentMethod === CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
            isWalletAndRazorpayTooSmall) ||
          (paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY &&
            isRazorpayOnlyTooSmall) ? (
            <>
              You can't proceed with this payment method as a payment of less
              than Rs 1 is not allowed with Razorpay.
            </>
          ) : paymentMethod === CART_PAYMENT_METHOD.WALLET_ONLY ? (
            <>
              Once you click on continue, the amount will instantly deduct from
              the wallet.
            </>
          ) : (
            <>
              The selected payment preferences cannot be modified after clicking
              'Continue'.
            </>
          )}
        </div>
      </div>

      {/* BUTTON */}
      <div className="pld-center">
        <Button
          raised
          className="zy-btn-orange pld-main-btn"
          disabled={
            !paymentMethod ||
            paymentMethod === -1 ||
            paymentMethod === CART_PAYMENT_METHOD.WALLET_DISABLED ||
            (paymentMethod === CART_PAYMENT_METHOD.WALLET_AND_RAZORPAY &&
              isWalletAndRazorpayTooSmall) ||
            (paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY &&
              isRazorpayOnlyTooSmall)
          }
          onClick={() => {
            if (
              !paymentMethod ||
              paymentMethod === CART_PAYMENT_METHOD.WALLET_DISABLED
            )
              return;

            const useWallet =
              paymentMethod !== CART_PAYMENT_METHOD.RAZORPAY_ONLY;

            // The approve payload (paymentDetails) is derived from useWallet in
            // CartPaymentLinkDialog.approveCart via getCartPaymentDetails — no
            // need to carry it here.
            const option = { useWallet };

            // RAZORPAY_ONLY whose net top-up is 0 is an overall refund — nothing
            // to collect, so no link is generated: skip the share-link screen and
            // approve directly (terminal SUCCESS_WITHOUT_LINK, like the wallet
            // debit / refund-only paths).
            const isRazorpayOnlyRefund =
              paymentMethod === CART_PAYMENT_METHOD.RAZORPAY_ONLY &&
              razorpayOnlyAmount <= 0;

            const nextScreen =
              paymentMethod === CART_PAYMENT_METHOD.WALLET_ONLY ||
              isRazorpayOnlyRefund
                ? CART_SCREEN_STATE.SUCCESS_WITHOUT_LINK
                : CART_SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS;

            goNext(nextScreen, option);
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default CartSelectPaymentMethod;
